import React, { useEffect, useRef } from 'react';
import { useData } from '../context/DataContext';
import styles from './Events.module.css';

const MONTH_TR = ['OCA','ŞUB','MAR','NİS','MAY','HAZ','TEM','AĞU','EYL','EKİ','KAS','ARA'];

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return { day: d.getDate(), month: MONTH_TR[d.getMonth()] };
}

export default function Events() {
  const { events, pastEvents, settings } = useData();
  const fadeRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add(styles.visible); }),
      { threshold: 0.1 }
    );
    fadeRefs.current.forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [events]);

  const addRef = el => { if (el && !fadeRefs.current.includes(el)) fadeRefs.current.push(el); };

  const active = events.filter(e => e.active);
  const featured = active.find(e => e.featured) || active[0];
  const upcoming = active.filter(e => e.id !== featured?.id);

  if (active.length === 0) return null;

  // Split heading for styling
  const headingParts = (settings.eventsSectionHeading || 'YAKLAŞAN ETKİNLİKLER').split(' ');
  const headingFirst = headingParts.slice(0, -1).join(' ');
  const headingLast = headingParts[headingParts.length - 1];

  return (
    <section className={styles.events} id="events">
      <div className={styles.inner}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <div className="section-label">{settings.eventsSectionLabel || 'Etkinlikler'}</div>
            <h2 className={styles.heading}>
              {headingFirst}<br /><span>{headingLast}</span>
            </h2>
          </div>
          <p className={styles.headerDesc}>
            {settings.eventsDesc || 'Workshop\'lar, özel günler ve stüdyo etkinlikleri. Kaçırmak istemediğin anlara buradan ulaş.'}
          </p>
        </div>

        {/* Featured big card */}
        {featured && (() => {
          const { day, month } = formatDate(featured.date);
          const spotsLeft = featured.capacity ? featured.capacity - featured.registered : null;
          return (
            <div className={`${styles.featured} ${styles.fadeUp}`} ref={addRef}>
              <div className={styles.featuredImg}>
                {featured.image
                  ? <img src={featured.image} alt={featured.title} className={styles.featuredPhoto} />
                  : <div className={styles.featuredBg} />
                }
                <div className={styles.featuredOverlay}>
                  <span className={styles.typeBadge}>{featured.type}</span>
                  <div className={styles.bigDate}>
                    <span className={styles.bigDay}>{day}</span>
                    <span className={styles.bigMonth}>{month}</span>
                  </div>
                </div>
              </div>
              <div className={styles.featuredInfo}>
                <div className={styles.metaRow}>
                  <span className={styles.catLabel}>{featured.type}</span>
                  {featured.capacity && (
                    <span className={styles.capacityBadge}>Sınırlı Kontenjan</span>
                  )}
                </div>
                <h3 className={styles.featuredTitle}>{featured.title}</h3>
                <p className={styles.featuredDesc}>{featured.description}</p>
                <div className={styles.detailsList}>
                  <div className={styles.detailItem}><span className={styles.detailIcon}>◷</span><span>{featured.time}</span></div>
                  <div className={styles.detailItem}><span className={styles.detailIcon}>◎</span><span>{featured.location}</span></div>
                  <div className={styles.detailItem}><span className={styles.detailIcon}>◈</span><span>{featured.price}</span></div>
                </div>
                <div className={styles.featuredActions}>
                  <a href="#contact" className="btn-primary">{featured.ctaLabel}</a>
                  {spotsLeft !== null && (
                    <span className={styles.spotsLeft}>
                      ⬤ {featured.registered} / {featured.capacity} Kontenjan Dolu
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Upcoming grid */}
        {upcoming.length > 0 && (
          <div className={styles.grid}>
            {upcoming.map((ev, i) => {
              const { day, month } = formatDate(ev.date);
              return (
                <div
                  key={ev.id}
                  className={`${styles.card} ${styles.fadeUp}`}
                  ref={addRef}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className={styles.cardHead}>
                    <div className={styles.cardDate}>
                      <span className={styles.cardDay}>{day}</span>
                      <span className={styles.cardMonth}>{month}</span>
                    </div>
                    <span className={styles.cardType}>{ev.type}</span>
                  </div>
                  <div className={styles.cardBody}>
                    <h4 className={styles.cardTitle}>{ev.title}</h4>
                    <p className={styles.cardDesc}>{ev.description}</p>
                    <div className={styles.cardMeta}>
                      <span>{ev.time}</span>
                      <span className={styles.metaDot}>·</span>
                      <span>{ev.location}</span>
                    </div>
                  </div>
                  <div className={styles.cardFooter}>
                    <a href="#contact" className={styles.cardBtn}>{ev.ctaLabel} →</a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Past events */}
        {pastEvents.length > 0 && (
          <div className={styles.pastStrip}>
            <span className={styles.pastLabel}>GEÇMİŞ ETKİNLİKLER</span>
            <div className={styles.pastItems}>
              {pastEvents.map((pe, i) => (
                <React.Fragment key={pe.id}>
                  <span className={styles.pastItem}>{pe.title} — {pe.date}</span>
                  {i < pastEvents.length - 1 && <span className={styles.pastDot}>✕</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
